import React from 'react';
import { computed, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import Button from '../Button/Button.js';
import Icon from '../Icon/Icon.js';
import { ITranslationContextValue } from '../Translate/models/index.js';
import Translate from '../Translate/Translate.js';
import { TranslationContext } from '../Translate/Translate.js';

import { IPillConfiguration, IPillProps, IPillState } from './models/index.js';
import DefaultWrapper from './styledComponents/DefaultWrapper.js';
import PrimaryWrapper from './styledComponents/PrimaryWrapper.js';
import SecondaryWrapper from './styledComponents/SecondaryWrapper.js';
import TertiaryWrapper from './styledComponents/TertiaryWrapper.js';
import Value from './styledComponents/Value.js';
import Wrapper from './styledComponents/Wrapper.js';
import { PILL_CATEGORY_KEY, PILL_COMPONENT_KEY } from './Pill.meta.js';

export class Pill extends ComponentBase<IPillProps, IPillState, IPillConfiguration> {
	categoryKey = PILL_CATEGORY_KEY;
	componentKey = PILL_COMPONENT_KEY;

	translationContext: ITranslationContextValue | undefined;

	constructor(props: IPillProps) {
		super(props);

		makeObservable(this, {
			ariaLabel: computed,
		});
	}
	get ariaLabel() {
		const { label, value, children } = this.props;
		let labelText = `${label} ${value || children}`;
		if (this.translationContext) {
			const translatedLabel = label && this.translationContext.translate(label);
			let translatedValue = '';
			if (value) {
				translatedValue = this.translationContext.translate(value);
			} else if (children) {
				translatedValue = this.translationContext.translate(children);
			}
			labelText = `${translatedLabel || ''} ${translatedValue}`;
		}
		return labelText;
	}

	render() {
		const {
			className,
			children,
			value,
			icon,
			iconPosition = 'left',
			onClick,
			onKeyDown,
			priority,
			label,
			closeButton,
		} = this.props;
		const pillIcon = <Icon {...icon} aria-hidden={true} />;
		const pillId = `pill_${label}_${value}`;
		// eslint-disable-next-line @typescript-eslint/naming-convention
		let PriorityWrapper = DefaultWrapper;
		if (priority === 'primary') {
			PriorityWrapper = PrimaryWrapper;
		}
		if (priority === 'secondary') {
			PriorityWrapper = SecondaryWrapper;
		}
		if (priority === 'tertiary') {
			PriorityWrapper = TertiaryWrapper;
		}
		return (
			<TranslationContext.Consumer>
				{(translationContext) => {
					this.translationContext = translationContext;
					return (
						<Wrapper
							className={className}
							onClick={onClick}
							onKeyDown={onKeyDown}
							aria-label={this.ariaLabel}
							role={'text'}
						>
							<PriorityWrapper>
								{icon && iconPosition === 'left' && pillIcon}
								<Value id={pillId}>
									<Translate>{value || children}</Translate>
								</Value>
								{icon && iconPosition === 'right' && pillIcon}
								{closeButton && <Button {...closeButton} describedby={pillId} />}
							</PriorityWrapper>
						</Wrapper>
					);
				}}
			</TranslationContext.Consumer>
		);
	}
}

export default InjectedStyledComponent(observer(Pill), PILL_CATEGORY_KEY, PILL_COMPONENT_KEY);
